import { useEffect, useMemo, useRef } from 'react';
import { ICartlinesViewProps } from '../../../modules/ext-cart/components/ext-cart-line-items';
import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { useMappingService, useTrackingService } from './serviceHooks';

const getCartSignature = (cartLines: ICartlinesViewProps[] | undefined) => {
    if (!cartLines) {
        return '';
    }
    return cartLines
        .map(({ data }) => `${data?.cartline.TotalAmount}-${data?.cartline.ItemId}-${data?.cartline.Quantity}`)
        .sort()
        .join('|');
};

export const useCartTracker = (
    subtotalAmount: number | undefined,
    cartLines: ICartlinesViewProps[] | undefined,
    isInitialized: boolean,
    request: IRequestContext
) => {
    const currentSignature = useMemo(() => {
        return getCartSignature(cartLines);
    }, [cartLines]);

    const previousCartLinesSignatureRef = useRef<string>();
    const hasInitializedRef = useRef(false);

    const relewiseTrackingService = useTrackingService(request);
    const relewiseMappingService = useMappingService(request);

    useEffect(() => {
        if (subtotalAmount === undefined || !isInitialized) {
            return;
        }

        // Initialize ref when it has been initialized
        if (!hasInitializedRef.current) {
            previousCartLinesSignatureRef.current = currentSignature;
            hasInitializedRef.current = true;
            return;
        }

        const hasCartChanged = currentSignature !== previousCartLinesSignatureRef.current;
        if (!hasCartChanged) {
            return;
        }

        const relewiseCart = relewiseMappingService.mapToRelewiseCart(subtotalAmount, cartLines);
        relewiseTrackingService.trackCart(relewiseCart);
        previousCartLinesSignatureRef.current = currentSignature;
    }, [subtotalAmount, currentSignature, relewiseTrackingService, relewiseMappingService]);
};
